import { claimIdentity } from '../../../webmodule-common/other/api/user-security';
import { containsSameText, isEmptyOrSpace, sameText } from '../../../webmodule-common/other/ui/string-helper-functions';

import { userDataStore } from './current-user-data-store';

export interface LicensePoolData {
  totalLicenses: number;
  licensesAllocatedToDealer: number;
  licensesUsedByDealer: number;
}

/**
 * A tenant's allocation should only be counted when it has real allocation data.
 * `-1` is the sentinel for "license pool/license not owned by this supplier" (e.g. Cyncly);
 * a missing/undefined value means the tenant has no allocation yet. Both are excluded so they
 * can't poison aggregate sums (`+= undefined` => NaN).
 */
function tenantHasAllocation(tenantInfo: {
  licensePoolAllocated: number | undefined;
  licensePoolAllocations: number | undefined;
}): boolean {
  return (
    tenantInfo.licensePoolAllocated !== -1 &&
    Number.isFinite(tenantInfo.licensePoolAllocations) &&
    tenantInfo.licensePoolAllocations !== -1
  );
}

export async function getLicenseData(deploymentId?: number): Promise<LicensePoolData> {
  const data: LicensePoolData = {
    totalLicenses: 0,
    licensesAllocatedToDealer: 0,
    licensesUsedByDealer: 0
  };

  await userDataStore.loadCoreDetails();

  const tentantList = userDataStore.allFranchiseeDetails.tenantList;
  const deployments =
    deploymentId === undefined
      ? (tentantList.deployments ?? [])
      : (tentantList.deployments?.filter(x => x.dealerDeploymentId === deploymentId) ?? []);

  deployments.forEach(deployment => {
    deployment.licensePools?.forEach(licensePool => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const licenseCount = licensePool.licensePool!.concurrentUsers;
      data.totalLicenses += licenseCount;
    });

    // Allocated is the sum of what each franchisee has actually been allocated from the pool, and
    // Used is the number of active users across those franchisees. Both are derived from per-tenant
    // data and avoid the pool's availableAllocations/availableUsers fields, which can be missing from
    // the payload (and reflect the real, potentially over-subscribed, allocation).
    deployment.tenants?.forEach(tenantInfo => {
      if (tenantHasAllocation(tenantInfo)) {
        data.licensesAllocatedToDealer += tenantInfo.licensePoolAllocations;
        data.licensesUsedByDealer +=
          deployment.userLoginInformation?.filter(user => user.tenantId === tenantInfo.tenantId && user.active)
            .length ?? 0;
      }
    });
  });
  return data;
}

export async function getLicenseDataByBranch(
  deploymentId?: number,
  franchiseeId?: string,
  branchIds?: string[]
): Promise<LicensePoolData> {
  const data: LicensePoolData = {
    totalLicenses: 0,
    licensesAllocatedToDealer: 0,
    licensesUsedByDealer: 0
  };

  await userDataStore.loadCoreDetails();
  const cacheData = userDataStore.allFranchiseeDetails;

  const tentantList = cacheData.tenantList;
  const deployments =
    deploymentId === undefined
      ? (tentantList.deployments ?? [])
      : (tentantList.deployments?.filter(x => x.dealerDeploymentId === deploymentId) ?? []);

  deployments.forEach(deployment => {
    deployment.tenants?.forEach(tenantInfo => {
      const franchiseeDeploymentConfiguration = cacheData.franchisees.find(
        x =>
          x.franchiseeTenantId === tenantInfo.tenantId &&
          x.dealerDeploymentId === deployment.dealerDeploymentId &&
          (isEmptyOrSpace(franchiseeId) || x.id === franchiseeId)
      );

      const branches = cacheData.branches.filter(
        b =>
          b.dealerDeploymentId === franchiseeDeploymentConfiguration?.dealerDeploymentId &&
          b.franchiseeTenantId === franchiseeDeploymentConfiguration?.franchiseeTenantId &&
          // Case-insensitive: the territory branch ids come from a different store than the branch cache.
          (branchIds === undefined || branchIds.length === 0 || containsSameText(b.branchId, branchIds))
      );

      const tenantUsers = deployment.userLoginInformation?.filter(x => x.tenantId === tenantInfo.tenantId) ?? [];

      // Must match isSameBranch below: comparing exactly here would let a case-differing claim
      // count on its own branch AND again on main, inflating the licenses-used total.
      function isInvalidBranch(id: string): boolean {
        return branches.find(c => sameText(c.branchId, id)) === undefined;
      }

      let activeBranchUsersCount = 0;
      branches.forEach((branch, index) => {
        const isMainBranch = index === 0;

        function isSameBranch(claims: { [key: string]: string } | null) {
          const branchId = claims?.[claimIdentity.branchId] ?? '';
          return sameText(branchId, branch.branchId) || (isInvalidBranch(branchId) && isMainBranch);
        }

        const branchUsers = tenantUsers.filter(x => isSameBranch(x.claims));
        activeBranchUsersCount += branchUsers.filter(x => x.active).length ?? 0;
      });
      if (tenantHasAllocation(tenantInfo)) {
        data.licensesUsedByDealer += activeBranchUsersCount;
        data.licensesAllocatedToDealer += tenantInfo.licensePoolAllocations;
      }
    });

    deployment.licensePools?.forEach(licensePool => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const licenseCount = licensePool.licensePool!.concurrentUsers;
      data.totalLicenses += licenseCount;
    });
  });
  return data;
}
